import { useEffect, useState } from "react";
import { useRemoteConfig } from "../../../context/RemoteConfigContext";

/**
 * Custom hook to check if user needs to complete payment registration
 *
 * @param {Object} userData - User data object from Firestore
 * @returns {Object} Object containing payment check results
 */
export const usePaymentCheck = (userData) => {
  const { paymentsWebActive } = useRemoteConfig();
  const [needsPayment, setNeedsPayment] = useState(false);
  const [needsNotification, setNeedsNotification] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [registrationDocId, setRegistrationDocId] = useState(null);
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState("");
  const [gracePeriodDaysRemaining, setGracePeriodDaysRemaining] = useState(0);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);
  const [billingFailureDaysRemaining, setBillingFailureDaysRemaining] =
    useState(0);

  // Run payment check when userData changes
  useEffect(() => {
    if (userData) {
      checkPaymentStatus(userData);
    } else {
      setIsLoading(false);
    }
  }, [userData]);

  const checkPaymentStatus = (userDataToCheck) => {
    try {
      // If payments are disabled via Remote Config, allow all users freely
      if (!paymentsWebActive) {
        setNeedsPayment(false);
        setNeedsNotification(false);
        setIsLoading(false);
        return;
      }

      const { userType } = userDataToCheck;

      // Handle coaches and managers separately for clarity
      if (userType === "coach") {
        handleCoachPaymentCheck(userDataToCheck);
      } else {
        handleManagerPaymentCheck(userDataToCheck);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      // Only show payment modal on error if payments are enabled
      if (paymentsWebActive) {
        setNeedsPayment(true);
      }
      setIsLoading(false);
    }
  };

  /**
   * Handle payment check for coaches - they inherit payment status from their manager
   */
  const handleCoachPaymentCheck = (userDataToCheck) => {
    const managerData = userDataToCheck?.managerData;

    // If manager data is not available, keep loading state until it's loaded
    if (!managerData) {
      // Keep loading state - don't show notification yet
      setIsLoading(true);
      setNeedsNotification(false);
      setNeedsPayment(false);
      return;
    }

    const managerPaymentDetails = managerData.paymentDetails;
    const managerCreatedAt = managerData.userCreatedAt;

    // Check if manager has special payment plan (highest priority)
    if (managerPaymentDetails?.specialPaymentPlan === true) {
      setCurrentScenario("H");
      setNeedsNotification(false);
      setNeedsPayment(false);
      setIsLoading(false);
      return;
    }

    // Check if manager has basic payment info
    if (!hasValidManagerPaymentData(managerPaymentDetails, managerCreatedAt)) {
      setNeedsNotification(true);
      setNeedsPayment(false);
      setIsLoading(false);
      return;
    }

    // Check if manager is suspended first (highest priority for coaches)
    if (managerPaymentDetails?.userSuspended === true) {
      const gracePeriodExpired = checkGracePeriodExpired(
        managerPaymentDetails.lastPaymentDate
      );

      if (gracePeriodExpired) {
        setCurrentScenario("F");
        setNeedsNotification(true);
        setNeedsPayment(false);
        setIsSuspended(true);
        setSuspensionReason("grace_period_expired");
        setGracePeriodDaysRemaining(0);
        setIsLoading(false);
        return;
      } else {
        const remainingDays = calculateGracePeriodDaysRemaining(
          managerPaymentDetails.lastPaymentDate
        );
        setCurrentScenario("E");
        setNeedsNotification(false);
        setNeedsPayment(false);
        setIsSuspended(true);
        setSuspensionReason("within_grace_period");
        setGracePeriodDaysRemaining(remainingDays);
        setIsLoading(false);
        return;
      }
    }

    // Reset suspension status for coaches when manager is not suspended
    setIsSuspended(false);
    setSuspensionReason("");

    // Check manager's payment status and apply to coach (for non-suspended accounts)
    const managerScenario = determineManagerScenario(
      managerPaymentDetails,
      managerCreatedAt
    );
    setCurrentScenario(managerScenario);

    // Coaches show notifications for scenarios that require manager action
    // Updated to include new scenarios A1, A2, D1, D2
    // Note: A2 and D1 are dismissible scenarios - coaches can continue using dashboard
    // Scenarios B, D2, F, G require immediate manager action and block coach access
    // Scenario H (special payment plan) allows free usage for both managers and coaches
    const scenariosRequiringNotification = ["B", "D2", "F", "G"];
    const shouldNotify =
      scenariosRequiringNotification.includes(managerScenario);

    setNeedsNotification(shouldNotify);
    setNeedsPayment(false); // Coaches never show payment modals

    setIsLoading(false);
  };

  /**
   * Handle payment check for managers - they have their own payment status
   */
  const handleManagerPaymentCheck = (userDataToCheck) => {
    const { paymentDetails, userCreatedAt } = userDataToCheck;
    // Check for special payment plan first (highest priority)
    if (paymentDetails?.specialPaymentPlan === true) {
      setCurrentScenario("H");
      setNeedsPayment(false);
      setNeedsNotification(false);
      setIsLoading(false);
      return;
    }

    // Check for suspended account first (highest priority)
    if (paymentDetails?.userSuspended === true) {
      handleSuspendedAccount(paymentDetails);
      return;
    }

    // Reset suspended state
    setIsSuspended(false);
    setSuspensionReason("");

    // Check basic payment requirements
    if (!hasValidPaymentData(paymentDetails, userCreatedAt)) {
      setNeedsPayment(true);
      setIsLoading(false);
      return;
    }

    // Determine the scenario based on payment status
    const scenario = determineManagerScenario(paymentDetails, userCreatedAt);
    setCurrentScenario(scenario);

    // Set payment requirements based on scenario
    // Updated to handle new scenarios A1, A2, D1, D2
    // Scenario H (special payment plan) never requires payment
    const scenariosRequiringPayment = ["A2", "B", "D1", "D2", "F", "G"];
    const needsPaymentResult = scenariosRequiringPayment.includes(scenario);

    setNeedsPayment(needsPaymentResult);

    // Set registration document ID if payment is needed
    if (
      scenariosRequiringPayment.includes(scenario) &&
      paymentDetails.registrationDocId
    ) {
      setRegistrationDocId(paymentDetails.registrationDocId);
    }

    setIsLoading(false);
  };

  /**
   * Determine the scenario based on payment details and creation date
   */
  const determineManagerScenario = (paymentDetails, userCreatedAt) => {
    const creationDate = convertToDate(userCreatedAt);

    if (!creationDate) {
      return "G"; // Missing data
    }

    // Calculate trial end date
    const trialEndDate = new Date(creationDate);
    trialEndDate.setDate(
      trialEndDate.getDate() + (paymentDetails.trialPeriodInMonths || 3) * 30
    );

    const now = new Date();
    const trialExpired = now > trialEndDate;
    const hasValidToken = paymentDetails.token && paymentDetails.token !== "";
    const hasBillingFailure = paymentDetails.lastBillingStatus === "failure";

    // Calculate days remaining in trial period
    const trialDaysRemaining = Math.ceil(
      (trialEndDate - now) / (1000 * 60 * 60 * 24)
    );
    setTrialDaysRemaining(Math.max(0, trialDaysRemaining));

    // Scenario A1/A2: User in trial period
    if (!trialExpired) {
      // Check if trial expires within 7 days (Scenario A2)
      if (trialDaysRemaining <= 7) {
        return "A2";
      }
      return "A1";
    }

    // Scenario B: Trial expired, no payment method
    if (trialExpired && !hasValidToken) {
      return "B";
    }

    // Scenario D: Active subscription but billing failure
    if (trialExpired && hasValidToken && hasBillingFailure) {
      // Check if billing failure is within 7 days (Scenario D1)
      const lastBillingAttemptDate = convertToDate(
        paymentDetails.lastBillingAttemptDate
      );
      if (lastBillingAttemptDate) {
        const billingFailureDays = Math.ceil(
          (now - lastBillingAttemptDate) / (1000 * 60 * 60 * 24)
        );
        setBillingFailureDaysRemaining(Math.max(0, billingFailureDays));

        if (billingFailureDays <= 7) {
          return "D1";
        }
      }
      return "D2";
    }

    // Scenario C: Active subscription with valid payment method and successful billing
    if (trialExpired && hasValidToken && !hasBillingFailure) {
      return "C";
    }

    // Default to scenario G for any other case
    return "G";
  };

  /**
   * Get scenario description in Hebrew
   */
  const getScenarioDescription = (scenario) => {
    const descriptions = {
      A1: "משתמש בתקופת ניסיון - אפשור שימוש רגיל",
      A2: "משתמש בתקופת ניסיון - תזכורת תשלום (בעוד X ימים תקופת הניסיון תיגמר)",
      B: "תקופת ניסיון הסתיימה - נדרש להסדיר אמצעי תשלום",
      C: "מנוי פעיל עם אמצעי תשלום תקין - אפשור שימוש רגיל",
      D1: "מנוי פעיל עם אמצעי תשלום לא תקין - תקופת חסד (בעוד X ימים נדרש תשלום)",
      D2: "מנוי פעיל עם אמצעי תשלום לא תקין - נדרש להסדיר מחדש",
      E: "משתמש מושהה במהלך תקופת החסד - אפשור שימוש עם תזכורת",
      F: "משתמש מושהה לאחר תקופת החסד - נדרש לחדש מנוי",
      G: "מידע תשלומים חסר/הושחת - נדרש להסדיר מחדש",
      H: "משתמש עם תוכנית תשלום מיוחדת - אפשור שימוש רגיל",
    };
    return descriptions[scenario] || "תרחיש לא ידוע";
  };

  /**
   * Handle suspended account logic
   */
  const handleSuspendedAccount = (paymentDetails) => {
    const gracePeriodExpired = checkGracePeriodExpired(
      paymentDetails.lastPaymentDate
    );

    if (gracePeriodExpired) {
      setIsSuspended(true);
      setSuspensionReason("grace_period_expired");
      setGracePeriodDaysRemaining(0);
      setNeedsPayment(true);
      setCurrentScenario("F");
    } else {
      const remainingDays = calculateGracePeriodDaysRemaining(
        paymentDetails.lastPaymentDate
      );
      setIsSuspended(true);
      setSuspensionReason("within_grace_period");
      setGracePeriodDaysRemaining(remainingDays);
      setNeedsPayment(false);
      setCurrentScenario("E");
    }

    setIsLoading(false);
  };

  /**
   * Check if manager has valid payment data (for coaches)
   */
  const hasValidManagerPaymentData = (paymentDetails, userCreatedAt) => {
    if (!paymentDetails || !userCreatedAt) {
      return false;
    }

    // Check required fields
    const requiredFields = ["didPay", "trialPeriodInMonths", "token"];
    const missingFields = requiredFields.filter(
      (field) => !(field in paymentDetails)
    );

    if (missingFields.length > 0) {
      return false;
    }

    // Validate data types
    if (
      typeof paymentDetails.didPay !== "boolean" ||
      typeof paymentDetails.trialPeriodInMonths !== "number" ||
      typeof paymentDetails.token !== "string"
    ) {
      return false;
    }

    return true;
  };

  /**
   * Check if manager has valid payment data (for managers)
   */
  const hasValidPaymentData = (paymentDetails, userCreatedAt) => {
    if (!paymentDetails || !userCreatedAt) {
      return false;
    }

    // Check required fields
    const requiredFields = ["didPay", "trialPeriodInMonths", "token"];
    const missingFields = requiredFields.filter(
      (field) => !(field in paymentDetails)
    );

    if (missingFields.length > 0) {
      return false;
    }

    // Validate data types
    if (
      typeof paymentDetails.didPay !== "boolean" ||
      typeof paymentDetails.trialPeriodInMonths !== "number" ||
      typeof paymentDetails.token !== "string"
    ) {
      return false;
    }

    return true;
  };

  /**
   * Convert Firestore timestamp to Date object
   */
  const convertToDate = (timestamp) => {
    if (!timestamp) return null;
    try {
      if (timestamp.toDate) {
        return timestamp.toDate();
      } else if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === "string") {
        return new Date(timestamp);
      } else {
        return timestamp;
      }
    } catch (error) {
      console.warn("Error converting timestamp to date:", error);
      return null;
    }
  };

  /**
   * Check if grace period has expired (30 days since last payment)
   */
  const checkGracePeriodExpired = (lastPaymentDate) => {
    if (!lastPaymentDate) return true;

    try {
      const paymentDate = convertToDate(lastPaymentDate);
      if (!paymentDate) return true;

      const gracePeriodEndDate = new Date(paymentDate);
      gracePeriodEndDate.setDate(gracePeriodEndDate.getDate() + 30);

      const now = new Date();
      return now > gracePeriodEndDate;
    } catch (error) {
      console.error("Error checking grace period:", error);
      return true;
    }
  };

  /**
   * Calculate remaining days in grace period
   */
  const calculateGracePeriodDaysRemaining = (lastPaymentDate) => {
    if (!lastPaymentDate) return 0;

    try {
      const paymentDate = convertToDate(lastPaymentDate);
      if (!paymentDate) return 0;

      const gracePeriodEndDate = new Date(paymentDate);
      gracePeriodEndDate.setDate(gracePeriodEndDate.getDate() + 30);

      const now = new Date();
      const timeRemaining = gracePeriodEndDate - now;
      const daysRemaining = Math.ceil(timeRemaining / (1000 * 60 * 60 * 24));

      return Math.max(0, daysRemaining);
    } catch (error) {
      console.error("Error calculating grace period days remaining:", error);
      return 0;
    }
  };

  return {
    needsPayment,
    needsNotification,
    isLoading,
    registrationDocId,
    isSuspended,
    suspensionReason,
    gracePeriodDaysRemaining,
    currentScenario,
    trialDaysRemaining,
    billingFailureDaysRemaining,
  };
};
